/**
 * AIChatPracticeSessionProvider — owns the PracticeSession slice via its own useReducer.
 *
 * One of the four nested slice providers mounted by AIChatProvider. Consumers read the slice
 * through the named hook useAIChatPracticeSession(); there is no merged value, each slice
 * context is subscribed to directly.
 *
 * Action gating: `areActionsEnabled` (AIChatProvider passes isAIEnabled) gates the exposed
 * action set at call time via useGatedActions — state stays readable while writes are suppressed,
 * and the exposed identities never change. The slice itself is generic and knows nothing about
 * AI capability.
 *
 * Scoping the provider to the practice view (and rerouting useConversationEngine off global
 * practice state) is deferred to a later ticket; this carve only extracts the slice.
 */
import PropTypes from 'prop-types';
import React, { createContext, useMemo, useReducer } from 'react';
import { useGatedActions } from '../useGatedActions';
import { PRACTICE_SESSION_ACTIONS, getInitialPracticeSessionState, practiceSessionReducer } from './aiChatPracticeSessionReducer';
import type { PracticeSessionState } from './aiChatPracticeSessionTypes';

export type AIChatPracticeSessionActions = {
  setActivityInstanceId: (activityInstanceId: string | null) => void;
  setActivityName: (activityName: string) => void;
  setActivityExtraInfo: (extraInfo: unknown | null) => void; // ← TODO: Remove after testing
  setActivityId: (activityId: string | null) => void;
  setCompletedActivitiesCount: (count: number) => void;
  addActivityResult: (result: unknown) => void;
  updateSessionKCs: (kcs: unknown[]) => void;
  resetPracticeSession: () => void;
};

export type AIChatPracticeSessionValue = PracticeSessionState & AIChatPracticeSessionActions;

export const AIChatPracticeSessionContext = createContext<AIChatPracticeSessionValue | null>(null);

type AIChatPracticeSessionProviderProps = {
  areActionsEnabled?: boolean;
  children: React.ReactNode;
  initialState?: Partial<PracticeSessionState>;
};

function AIChatPracticeSessionProvider({ areActionsEnabled = true, children, initialState = {} }: AIChatPracticeSessionProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(practiceSessionReducer, initialState, getInitialPracticeSessionState);

  // Setters memoized once (deps `[]`) so their identity is stable for the component's lifetime —
  // `dispatch` is stable, so there is no reason to rebuild them per state change. Keeping them out
  // of the `[state]`-keyed value memo avoids churning consumer useCallback/useEffect deps.
  const actions = useMemo<AIChatPracticeSessionActions>(
    () => ({
      setActivityInstanceId: (activityInstanceId: string | null) => dispatch({ type: PRACTICE_SESSION_ACTIONS.SET_ACTIVITY_INSTANCE_ID, payload: activityInstanceId }),
      setActivityName: (activityName: string) => dispatch({ type: PRACTICE_SESSION_ACTIONS.SET_ACTIVITY_NAME, payload: activityName }),
      setActivityExtraInfo: (extraInfo: unknown | null) => dispatch({ type: PRACTICE_SESSION_ACTIONS.SET_ACTIVITY_EXTRA_INFO, payload: extraInfo }), // ← TODO: Remove after testing
      setActivityId: (activityId: string | null) => dispatch({ type: PRACTICE_SESSION_ACTIONS.SET_ACTIVITY_ID, payload: activityId }),
      setCompletedActivitiesCount: (count: number) => dispatch({ type: PRACTICE_SESSION_ACTIONS.SET_COMPLETED_ACTIVITIES_COUNT, payload: count }),
      addActivityResult: (result: unknown) => dispatch({ type: PRACTICE_SESSION_ACTIONS.ADD_ACTIVITY_RESULT, payload: result }),
      updateSessionKCs: (kcs: unknown[]) => dispatch({ type: PRACTICE_SESSION_ACTIONS.UPDATE_SESSION_KCS, payload: kcs }),
      resetPracticeSession: () => dispatch({ type: PRACTICE_SESSION_ACTIONS.RESET_PRACTICE_SESSION }),
    }),
    []
  );

  // Call-time gating keeps the exposed identities permanently stable: consumers that capture the
  // set once (empty-dep callbacks/effects) keep dispatching correctly after areActionsEnabled flips.
  const exposedActions = useGatedActions(actions, areActionsEnabled);

  const value = useMemo<AIChatPracticeSessionValue>(() => ({ ...state, ...exposedActions }), [state, exposedActions]);

  return <AIChatPracticeSessionContext.Provider value={value}>{children}</AIChatPracticeSessionContext.Provider>;
}

AIChatPracticeSessionProvider.propTypes = {
  areActionsEnabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  initialState: PropTypes.object,
};

export default AIChatPracticeSessionProvider;
